import AuthLayout from "@/layouts/AuthLayout";
import DashboardLayout from "@/layouts/DashboardLayout";
import HomeLayout from "@/layouts/HomeLayout";
import LocaleLayout from "@/layouts/LocaleLayout";
import { PATHS, buildRoute } from "@/utils/routes";
import LoadingScreen from "@c/shared/LoadingScreen";
import { useAuth } from "@h/useCtx";
import { useLanguageSync } from "@h/useLanguageSync";
import { useVanityHost } from "@h/useVanityHost";
import { AlertTriangle, Mail, RotateCcw } from "lucide-react";
import type { ReactNode } from "react";
import { Component, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { normalizeAppLang } from "@u/appLang";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { ConsentBanner } from "@c/shared/ConsentBanner";
import { OfflineBanner } from "@c/shared/OfflineBanner";
import { Toaster } from "sonner";
import Button from "./components/ui/button";
import { useApp } from "@h/useCtx";
import {
  AdminGuard,
  TourEditorProvider,
  NotFoundPage,
  PublicGuard,
  RequireAuth,
  ViewerGuard,
} from "./guards";
const OwnerHubPage = lazy(() => import("@p/landing/OwnerHubPage"));
const FaqPage = lazy(() => import("@p/landing/FaqPage"));
const BookDemoPage = lazy(() => import("@p/landing/BookDemoPage"));
const PortfolioPage = lazy(() => import("@p/landing/PortfolioPage"));
const ThreeSixtyProductPage = lazy(
  () => import("@p/landing/three-sixty/ThreeSixtyProductPage"),
);
const TourViewerPage = lazy(
  () => import("@p/viewer/three-sixty/TourViewerPage"),
);
const LoginPage = lazy(() => import("@p/auth/LoginPage"));
const OAuthCallbackPage = lazy(() => import("@p/auth/OAuthCallbackPage"));
const RecoverPasswordPage = lazy(() => import("@p/auth/RecoverPasswordPage"));
const TourListPage = lazy(() => import("@p/dashboard/ToursPage"));
const EditorPage = lazy(() => import("@p/dashboard/EditorPage"));
const SettingsPage = lazy(() => import("@p/dashboard/SettingsPage"));
const UsersPage = lazy(() => import("@p/dashboard/UsersPage"));
const UserDetailPage = lazy(() => import("@p/dashboard/UserDetailPage"));
const BookingsPage = lazy(() => import("@p/dashboard/BookingsPage"));
const BrandingPage = lazy(() => import("@p/dashboard/BrandingPage"));
const BillingPage = lazy(() => import("@p/dashboard/BillingPage"));
const AnalyticsPage = lazy(() => import("@p/dashboard/AnalyticsPage"));
const OverviewPage = lazy(() => import("@p/dashboard/OverviewPage"));
const HealthPage = lazy(() => import("@p/dashboard/HealthPage"));
const ContactsPage = lazy(() => import("@p/dashboard/ContactsPage"));
const ContactDetailPage = lazy(
  () => import("@p/dashboard/ContactDetailPage"),
);
const HelpPage = lazy(() => import("@p/dashboard/HelpPage"));
const DashboardShowcasePage = import.meta.env.DEV
  ? lazy(() => import("@p/dashboard/DashboardShowcasePage"))
  : null;
const PrivacyPolicyPage = lazy(() => import("@p/landing/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@p/landing/TermsOfServicePage"));
const BookingResponsePage = lazy(() => import("@p/_system/BookingResponsePage"));

// legacy /edit/:id and /dashboard/tours/edit/:id → /dashboard/tours/:id (keep query)
function EditRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`${buildRoute.detail(id ?? "")}${search}`} replace />;
}


const LegacySlugRedirect = lazy(() => import("@p/_system/LegacySlugRedirect"));

function LocaleRedirect({ to }: { to: string }) {
  const { i18n } = useTranslation();
  return <Navigate to={`/${normalizeAppLang(i18n.language)}${to}`} replace />;
}

// Index route: on a vanity host (`<slug>.andarlabs.com`) render the owner's
// branded hub; on the main domain render the marketing landing.
// Landing única: el index ES la landing .360; `/:lang/360` redirige acá.
function IndexRoute() {
  const { isVanity } = useVanityHost();
  return isVanity ? <OwnerHubPage /> : <ThreeSixtyProductPage />;
}

function AppRoutes() {
  const { loadingAuth } = useAuth();
  const location = useLocation();
  useLanguageSync();

  const isViewerRoute =
    location.pathname.startsWith("/v/") ||
    location.pathname.startsWith("/360/");
  if (loadingAuth && !isViewerRoute) return <LoadingScreen />;

  return (
    <Routes>
      {/* Public owner hub by username (all plans, product-neutral, no locale) */}
      <Route
        path={PATHS.ownerHub}
        element={
          <Suspense fallback={null}>
            <OwnerHubPage />
          </Suspense>
        }
      />

      {/* Legacy slug-based routes → redirect to short_id URL */}
      <Route path="/v/:slug" element={<Suspense fallback={null}><LegacySlugRedirect /></Suspense>} />

      {/* Redirect old bare paths → /:lang/... */}
      <Route path="/" element={<LocaleRedirect to="/" />} />
      <Route path="/faq" element={<LocaleRedirect to="/faq" />} />
      <Route path="/book" element={<LocaleRedirect to="/book" />} />
      <Route path="/portfolio" element={<LocaleRedirect to="/portfolio" />} />
      {/* /plans retirada: el bare path cae al index localizado. */}
      <Route path="/plans" element={<LocaleRedirect to="/" />} />
      <Route path="/privacy" element={<LocaleRedirect to="/privacy" />} />
      <Route path="/terms" element={<LocaleRedirect to="/terms" />} />

      {/* Locale-prefixed public routes */}
      <Route path={PATHS.localeRoot} element={<LocaleLayout />}>
        <Route element={<HomeLayout />}>
          <Route index element={<IndexRoute />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="book" element={<BookDemoPage />} />
          {/* Landing única: /:lang/360 → el index. */}
          <Route path="360" element={<Navigate to=".." replace />} />
          <Route path="portfolio" element={<PortfolioPage />} />
          {/* /plans retirada (2026-09-16): links viejos → el index. */}
          <Route path="plans" element={<Navigate to=".." replace />} />
          {/* legacy deep-link → portfolio */}
          <Route path="360/portfolio" element={<Navigate to="../portfolio" replace />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="terms" element={<TermsOfServicePage />} />
        </Route>
      </Route>

      <Route path={PATHS.bookingResponse} element={<BookingResponsePage />} />

      <Route
        path={PATHS.viewer}
        element={
          <Suspense fallback={null}>
            <PublicGuard>
              <ViewerGuard>
                <TourViewerPage />
              </ViewerGuard>
            </PublicGuard>
          </Suspense>
        }
      />

      <Route element={<AuthLayout />}>
        <Route path={PATHS.login} element={<LoginPage />} />
        {/* Sin alta pública (2026-09-16): las cuentas las crea el admin. */}
        <Route path={PATHS.register} element={<Navigate to={PATHS.login} replace />} />
        <Route path={PATHS.resetPassword} element={<RecoverPasswordPage />} />
      </Route>

      <Route path={PATHS.authCallback} element={<OAuthCallbackPage />} />

      <Route element={<RequireAuth />}>
        <Route element={<DashboardLayout />}>
          <Route path={PATHS.overview} element={<OverviewPage />} />
          <Route path={PATHS.tours} element={<TourListPage />} />
          <Route path={PATHS.edit} element={<EditRedirect />} />
          <Route path={PATHS.legacyTourEdit} element={<EditRedirect />} />
          <Route
            path={PATHS.detail}
            element={
              <TourEditorProvider>
                <EditorPage />
              </TourEditorProvider>
            }
          />
          <Route path={PATHS.settings} element={<SettingsPage />} />
          <Route
            path={PATHS.bookings}
            element={
              <div className="mx-auto w-full max-w-7xl">
                <BookingsPage />
              </div>
            }
          />
          <Route path={PATHS.branding} element={<BrandingPage />} />
          <Route path={PATHS.billing} element={<BillingPage />} />
          <Route
            path={PATHS.analytics}
            element={
              <div className="mx-auto w-full max-w-7xl">
                <AnalyticsPage />
              </div>
            }
          />
          <Route path={PATHS.health} element={<HealthPage />} />
          {/* /leads se fusiono en /contacts (2026-09-14). Quedan links
              externos y bookmarks: redirect, nunca 404. */}
          <Route
            path={PATHS.leads}
            element={<Navigate to={buildRoute.contacts()} replace />}
          />
          <Route
            path={PATHS.leadDetail}
            element={<Navigate to={buildRoute.contacts()} replace />}
          />
          <Route path={PATHS.contacts} element={<ContactsPage />} />
          <Route
            path={PATHS.contactDetail}
            element={<ContactDetailPage />}
          />
          <Route path={PATHS.help} element={<HelpPage />} />
          <Route path={PATHS.helpTopic} element={<HelpPage />} />
          <Route
            path={PATHS.users}
            element={
              <AdminGuard>
                <UsersPage />
              </AdminGuard>
            }
          />
          <Route
            path={PATHS.userDetail}
            element={
              <AdminGuard>
                <UserDetailPage />
              </AdminGuard>
            }
          />
          {/* Dev-only design-system gallery (stripped from prod bundle) */}
          {DashboardShowcasePage && (
            <Route
              path={PATHS.showcase}
              element={<DashboardShowcasePage />}
            />
          )}
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

const CrashScreen = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center h-dvh bg-background p-6 text-center animate-in fade-in duration-500">
      <div className="p-4 rounded-full bg-destructive/10 mb-6">
        <AlertTriangle className="w-12 h-12 text-destructive" />
      </div>

      <h1 className="text-3xl tracking-tight mb-3">{t("crash.title")}</h1>
      <p className="text-muted-foreground max-w-sm mb-8">
        {t("crash.description")}
      </p>

      <div className="flex flex-wrap gap-3">
        <Button design="outline" onClick={() => window.location.reload()}>
          <RotateCcw className="mr-2 h-4 w-4" /> {t("crash.reload")}
        </Button>
        <Button
          onClick={() =>
            (window.location.href = "mailto:support@andarlabs.com")
          }
        >
          <Mail className="mr-2 h-4 w-4" /> {t("crash.support")}
        </Button>
      </div>
    </div>
  );
};

class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: unknown, info: unknown) {
    // No external reporting service — surface crashes in the console at least.
    console.error("App crash:", error, info);
  }
  render() {
    return this.state.hasError ? <CrashScreen /> : this.props.children;
  }
}

export default function App() {
  const { theme } = useApp();
  return (
    // Router + NiceModal.Provider viven en AppProviders (main.tsx) — única
    // fuente del orden de providers, testeada en modalProviderTree.test.tsx.
    <>
      <AppErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <AppRoutes />
        </Suspense>
      </AppErrorBoundary>
      {/* Inside the Router: the banner links to /privacy, and a <Link> outside
          the router context throws (blank page for every first-time visitor). */}
      <ConsentBanner />
      <OfflineBanner />
      <Toaster
        position="top-center"
        theme={theme}
        richColors
        closeButton
        toastOptions={{
          style: {
            borderRadius: "1.25rem",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(16px)",
            fontWeight: "700",
          },
        }}
      />
    </>
  );
}
